import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { CampagneState, CampagneContent } from '../types/campagne';
import { format, addDays, isWeekend, nextMonday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { CampagneForm } from '../components/CampagneForm';
import { useAuth } from '../lib/supabase/AuthContext';
import { getCampaigns, createCampaign } from '../lib/supabase/campaignsService';
import { Database } from '../types/supabase';

type Campaign = Database['public']['Tables']['campaigns']['Row'];

const MAX_CAMPAGNES = 3;

const calculateLaunchDate = (creationDate: string | null): Date => {
  const baseDate = creationDate ? new Date(creationDate) : new Date();
  const launchDate = new Date(baseDate);
  launchDate.setDate(launchDate.getDate() + 7); // Lancement 7 jours après création
  return launchDate;
};

const MesCampagnes: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Vous ne pouvez pas créer plus de campagnes");

  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const { data, error } = await getCampaigns(user.id);
        
        if (error) {
          throw error;
        }
        
        setCampaigns(data || []);
      } catch (error: any) {
        console.error('Erreur lors du chargement des campagnes:', error);
        setError(`Une erreur est survenue: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [user]);

  const handleCreateCampagne = () => {
    if (campaigns.length >= MAX_CAMPAGNES) {
      setErrorMessage("Vous ne pouvez pas créer plus de " + MAX_CAMPAGNES + " campagnes");
      setShowError(true);
      return;
    }
    setOpenDialog(true);
  };

  const handleConfirmCreate = async (formData: Partial<CampagneContent>) => {
    if (!user) {
      setErrorMessage("Vous devez être connecté pour créer une campagne");
      setShowError(true);
      return;
    }
    
    try {
      setLoading(true);
      
      const { data, error } = await createCampaign({
      nom: formData.siteWeb || 'Nouvelle campagne',
        linkedin_copy: "",
        mail_copy: "",
        question_qualification: JSON.stringify([
        "Quel est votre processus actuel de gestion des prospects ?",
        "Combien de commerciaux composent votre équipe ?",
        "Quel est votre objectif principal en termes d'amélioration de votre processus commercial ?"
        ]),
        database_statut: 'En attente',
        linkedin_statut: 'En attente',
        mail_statut: 'En attente',
        lance: false
      }, user.id);
      
      if (error) {
        throw error;
      }
      
      if (data) {
        setCampaigns((prev) => [...prev, data]);
        setSelectedCampaign(data.id);
    setOpenDialog(false);
        navigate(`/campagne/${data.id}`);
      }
    } catch (error: any) {
      console.error('Erreur lors de la création de la campagne:', error);
      setErrorMessage(`Une erreur est survenue: ${error.message}`);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCampagne = (id: string) => {
    setSelectedCampaign(id);
    navigate(`/campagne/${id}`);
  };

  if (loading && campaigns.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4">
            Mes Campagnes
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateCampagne}
            disabled={campaigns.length >= MAX_CAMPAGNES || loading}
          >
            Créer une campagne
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {campaigns.length === 0 && !loading ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="h6" color="text.secondary">
              Vous n'avez pas encore de campagnes
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleCreateCampagne}
              sx={{ mt: 2 }}
              startIcon={<AddIcon />}
            >
              Créer votre première campagne
            </Button>
          </Box>
        ) : (
        <Grid container spacing={3}>
            {campaigns.map((campagne) => (
            <Grid item xs={12} sm={6} md={4} key={campagne.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {campagne.nom}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                      Créée le {campagne.created_at ? format(new Date(campagne.created_at), 'dd MMMM yyyy', { locale: fr }) : 'Date inconnue'}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Database: <span style={{ color: campagne.database_statut === 'Validé' ? 'green' : 'orange' }}>
                          {campagne.database_statut}
                        </span>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Copy Email: <span style={{ color: campagne.mail_statut === 'Validé' ? 'green' : 'orange' }}>
                          {campagne.mail_statut}
                        </span>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Copy LinkedIn: <span style={{ color: campagne.linkedin_statut === 'Validé' ? 'green' : 'orange' }}>
                          {campagne.linkedin_statut}
                        </span>
                    </Typography>
                    
                    {/* Statistiques de la campagne */}
                    <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                        📊 Statistiques:
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            📧 Mails: {campagne.nb_mails_envoyes || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            💼 LinkedIn: {campagne.nb_messages_linkedin || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            📞 Appels: {campagne.nb_appels || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            📅 RDV: {campagne.nb_rdv || 0}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    
                    <Typography variant="body2" sx={{ mt: 2, fontWeight: 'medium' }}>
                        Lancement prévu: {campagne.date_lancement ? 
                          format(new Date(campagne.date_lancement), 'dd MMMM yyyy', { locale: fr }) : 
                          format(calculateLaunchDate(campagne.created_at), 'dd MMMM yyyy', { locale: fr })}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    onClick={() => handleOpenCampagne(campagne.id)}
                      disabled={loading}
                  >
                    Ouvrir
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        )}
      </Paper>

      {/* Dialog de création de campagne */}
      <Dialog 
        open={openDialog} 
        onClose={() => !loading && setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Créer une nouvelle campagne</DialogTitle>
        <DialogContent>
          <CampagneForm
            onSubmit={handleConfirmCreate}
            onCancel={() => !loading && setOpenDialog(false)}
            loading={loading}
          />
        </DialogContent>
      </Dialog>

      {/* Message d'erreur */}
      <Snackbar 
        open={showError} 
        autoHideDuration={6000} 
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MesCampagnes; 